import type {
  PermissionCatalogAction,
  PermissionCatalogNode,
} from "../../permissions";

/**
 * Merges the permission catalogs contributed by every registered feature into a
 * single tree, joining nodes that share a segment at the same depth.
 *
 * Features declare their catalog rooted at the *business module* namespace, not
 * their own feature id (REBUILD-PLAN §5.5) — so `organization`, `roles`,
 * `subscriptions` and `catalog` all contribute a `core` root. Concatenating
 * those would render "Core" four times in the roles matrix, with four React
 * children keyed `"core"`, and would split one backend namespace across
 * unrelated-looking groups.
 *
 * First writer wins for `labelKey`: the features are merged in the registry's
 * stable id order, so the label is deterministic rather than dependent on
 * registration timing. Actions are unioned by id, preserving first-seen order.
 */
export function mergePermissionCatalogs(
  catalogs: readonly (readonly PermissionCatalogNode[])[],
): readonly PermissionCatalogNode[] {
  return mergeLevel(catalogs.flat());
}

function mergeLevel(
  nodes: readonly PermissionCatalogNode[],
): readonly PermissionCatalogNode[] {
  const bySegment = new Map<string, PermissionCatalogNode[]>();

  for (const node of nodes) {
    const existing = bySegment.get(node.segment);

    if (existing === undefined) {
      bySegment.set(node.segment, [node]);
    } else {
      existing.push(node);
    }
  }

  return [...bySegment.values()].map((group) => {
    const [first] = group as [
      PermissionCatalogNode,
      ...PermissionCatalogNode[],
    ];

    if (group.length === 1) {
      return first;
    }

    const children = mergeLevel(group.flatMap((node) => node.children ?? []));
    const actions = mergeActions(group.flatMap((node) => node.actions ?? []));

    return {
      segment: first.segment,
      labelKey: first.labelKey,
      ...(children.length > 0 ? { children } : {}),
      ...(actions.length > 0 ? { actions } : {}),
    };
  });
}

function mergeActions(
  actions: readonly PermissionCatalogAction[],
): readonly PermissionCatalogAction[] {
  const byId = new Map<string, PermissionCatalogAction>();

  for (const action of actions) {
    if (!byId.has(action.id)) {
      byId.set(action.id, action);
    }
  }

  return [...byId.values()];
}
